/**
 * The First 100 Days framework: phases, milestones, the meeting playbook, the
 * listening-tour questions and the priority dispositions (roadmap S-16).
 *
 * Moved here from the retired satellite (`first100days/src/data/framework.ts`,
 * DEP-043) with its words unchanged. What changed is where the work is kept:
 * the satellite kept progress in the browser; the app keeps it on Mandate's
 * record and the shared items (first100Days.ts).
 */

export type PhaseId = 'listen' | 'align' | 'lead' | 'solidify';

export interface Milestone {
  id: string;
  day: number;
  title: string;
  description: string;
}

export interface Phase {
  id: PhaseId;
  number: number;
  title: string;
  subtitle: string;
  /** First and last day of the phase. */
  days: [number, number];
  description: string;
  principles: string[];
  milestones: Milestone[];
}

export interface AgendaItem {
  time: string;
  topic: string;
  notes?: string;
}

export interface MeetingTemplate {
  id: string;
  title: string;
  timing: string;
  duration: string;
  purpose: string;
  agenda: AgendaItem[];
  doList: string[];
  dontList: string[];
  questions: string[];
}


// ─── Phase Definitions ───────────────────────────────────────────────────────

export const PHASES: Phase[] = [
  {
    id: 'listen',
    number: 1,
    title: 'Listen & Learn',
    subtitle: 'Days 1–30',
    days: [1, 30],
    description:
      `Before you lead, you must understand. The single biggest mistake new senior leaders make is acting before they've truly listened. Your mandate in this phase is to absorb — the culture, the people, the hidden dynamics, the unspoken rules.`,
    principles: [
      'Assume nothing; question everything you think you know',
      'Listen to understand, not to respond or fix',
      'Map the landscape before drawing conclusions',
      'Ask the same question of many different people',
      'Pay as much attention to what is not said as what is',
      'Your calendar is your strategy — protect time to think',
    ],
    milestones: [
      {
        id: 'listen-1',
        day: 1,
        title: 'Day One Clarity',
        description: 'Know your mandate. Have a clear, written answer to: what does success look like at 100 days?',
      },
      {
        id: 'listen-2',
        day: 5,
        title: 'Stakeholder Map Draft',
        description:
          'Identify your 20 most important relationships: up, lateral, down, external. Prioritize your first conversations.',
      },
      {
        id: 'listen-3',
        day: 10,
        title: 'First Team Meeting',
        description:
          'Hold your first team meeting. Listen, set expectations, don\'t make changes yet.',
      },
      {
        id: 'listen-4',
        day: 15,
        title: 'Listening Tour Underway',
        description:
          'Have had substantive 1:1 conversations with your direct reports and key peers.',
      },
      {
        id: 'listen-5',
        day: 30,
        title: '30-Day Synthesis',
        description:
          'Articulate what you\'ve heard in 3 categories: strengths to build on, tensions to resolve, opportunities to seize.',
      },
    ],
  },
  {
    id: 'align',
    number: 2,
    title: 'Align & Build',
    subtitle: 'Days 31–60',
    days: [31, 60],
    description:
      `You've listened. Now begin to shape. Share what you've heard, test your hypotheses, and start building the alignment that will make everything else possible. Relationships are built before they are needed.`,
    principles: [
      'Share your synthesis — tell people what you heard',
      'Earn credibility with small, visible wins before the big moves',
      'Build across before you push down',
      'Be explicit about how you will make decisions',
      'Create safety for people to tell you the truth',
      'Name the patterns you see without labeling the people',
    ],
    milestones: [
      {
        id: 'align-1',
        day: 35,
        title: 'Synthesis Shared',
        description:
          'Share a "what I\'ve heard" document or conversation with your team and key stakeholders.',
      },
      {
        id: 'align-2',
        day: 45,
        title: 'Operating Principles Set',
        description:
          'Articulate how you lead: decision rights, communication norms, how you give feedback.',
      },
      {
        id: 'align-3',
        day: 50,
        title: 'Early Win Delivered',
        description:
          'Identify and execute one visible improvement that builds credibility without disrupting the system.',
      },
      {
        id: 'align-4',
        day: 55,
        title: 'Lateral Relationships Built',
        description:
          'Have had substantive relationship-building conversations with your most critical peers.',
      },
      {
        id: 'align-5',
        day: 60,
        title: '60-Day Check-In',
        description:
          'Check in with your manager: are you on track? What have they observed? Where do they need more from you?',
      },
    ],
  },
  {
    id: 'lead',
    number: 3,
    title: 'Lead & Articulate',
    subtitle: 'Days 61–90',
    days: [61, 90],
    description:
      `You've earned enough trust to lead. Now articulate your strategy clearly, make harder decisions, and begin to establish the operating system your team will run on. This is when your fingerprints begin to appear.`,
    principles: [
      'Articulate direction clearly — ambiguity costs you twice',
      'Make decisions visible, even when uncomfortable',
      'Model the behavior you want from the team',
      'Address performance issues early, clearly, and with care',
      'Structure the work before you delegate it',
      'Say no to what doesn\'t matter so you can say yes to what does',
    ],
    milestones: [
      {
        id: 'lead-1',
        day: 65,
        title: 'Strategy Articulated',
        description:
          'Communicate your view of what matters and why. Even if the full strategy isn\'t set, your team needs direction.',
      },
      {
        id: 'lead-2',
        day: 70,
        title: 'Team Structure Reviewed',
        description:
          'Assess whether the team structure is right for what you need to accomplish.',
      },
      {
        id: 'lead-3',
        day: 75,
        title: 'Operating Rhythm Established',
        description:
          'Standing meetings, reporting cadences, and decision forums are in place.',
      },
      {
        id: 'lead-4',
        day: 80,
        title: 'Hard Conversations Had',
        description:
          'Performance issues surfaced and addressed. Don\'t let problems compound.',
      },
      {
        id: 'lead-5',
        day: 90,
        title: '90-Day Review',
        description:
          'Formal or informal review with your manager. What have you accomplished? What\'s the plan for the final stretch?',
      },
    ],
  },
  {
    id: 'solidify',
    number: 4,
    title: 'Solidify & Prepare',
    subtitle: 'Days 91–100',
    days: [91, 100],
    description:
      'The final phase isn\'t about doing more — it\'s about locking in what works and preparing for steady state. By Day 100, you should know your top priorities, have your team oriented, and have an operating system to execute through.',
    principles: [
      'Know your 3–5 highest-leverage priorities for the year',
      'Clarify what you will stop, start, and continue',
      'Have your team\'s operating system locked in',
      'Document what you\'ve learned for your own reference',
      'Prepare the bridge: what does ongoing execution look like?',
    ],
    milestones: [
      {
        id: 'solidify-1',
        day: 92,
        title: 'Priorities Locked',
        description:
          'Know and document your top 3–5 priorities for the next 6–12 months.',
      },
      {
        id: 'solidify-2',
        day: 95,
        title: 'Stop/Start/Continue',
        description:
          'Define explicitly what you will stop doing, start doing, and continue doing.',
      },
      {
        id: 'solidify-3',
        day: 98,
        title: 'Team Aligned on Direction',
        description:
          'Your team can articulate your shared direction without prompting.',
      },
      {
        id: 'solidify-4',
        day: 100,
        title: 'Day 100 Reflection',
        description:
          'Document your key insights, decisions made, and the operating system you\'ve built.',
      },
    ],
  },
]

// ─── Meeting Templates ────────────────────────────────────────────────────────

export const MEETING_TEMPLATES: MeetingTemplate[] = [
  {
    id: 'first-team',
    title: 'First Team Meeting',
    timing: 'Week 1–2',
    duration: '60–90 min',
    purpose:
      'Establish your presence, create safety, begin building trust. This is a listening meeting, not a leading meeting.',
    agenda: [
      { time: '0–5 min', topic: 'Welcome & framing', notes: 'Tell people what this meeting is and is not about.' },
      { time: '5–20 min', topic: 'Your background & style', notes: 'Brief professional history, what drives you, how you work. Be human.' },
      { time: '20–40 min', topic: 'What you want to learn', notes: 'The questions you are here to answer. Ask for their help.' },
      { time: '40–60 min', topic: 'What they want you to know', notes: 'Open floor. What do they most want you to understand about this team?' },
      { time: '60–75 min', topic: 'What\'s next', notes: 'How you\'ll follow up, when you\'ll meet again, what to expect in the coming weeks.' },
    ],
    doList: [
      'Show up prepared and on time',
      'Be genuinely curious',
      'Write things down visibly — it signals you take them seriously',
      'Name what you don\'t know yet',
      'Be clear about what you will and won\'t decide in this meeting',
      'Thank them for their candor',
    ],
    dontList: [
      'Announce changes or your "vision" — too early',
      'Ask them to assess each other',
      'Over-promise what you\'ll do with what you hear',
      'Let it become a complaint session without acknowledging the themes',
      'Dominate the conversation',
    ],
    questions: [
      'What is this team most proud of?',
      'What are the most important things you\'re working on right now?',
      'What do you wish leaders here understood better?',
      'What\'s one thing that would make your work meaningfully better?',
      'What should I not break?',
    ],
  },
  {
    id: 'first-101',
    title: 'First 1:1 with Direct Reports',
    timing: 'Weeks 1–3',
    duration: '45–60 min',
    purpose:
      'Understand each person: their work, their ambitions, how they see the world, and how you can be useful to them.',
    agenda: [
      { time: '0–5 min', topic: 'Check in', notes: 'How are they? What\'s on their mind?' },
      { time: '5–25 min', topic: 'Their world', notes: 'Their role, what they\'re working on, what they\'re proud of.' },
      { time: '25–40 min', topic: 'Their perspective', notes: 'What do they see that you should know? What\'s working, what\'s not?' },
      { time: '40–50 min', topic: 'Their asks', notes: 'What do they need from you to be successful?' },
      { time: '50–60 min', topic: 'Setting up ongoing 1:1s', notes: 'Cadence, format, what makes these useful for them.' },
    ],
    doList: [
      'Prepare — know their background and current projects',
      'Ask about their career aspirations, not just their current work',
      'Be clear that you\'re listening, not yet evaluating',
      'Follow up on what they tell you',
      'Be on time and give them your full attention',
    ],
    dontList: [
      'Turn it into a status report',
      'Make judgments about their predecessor or peers',
      'Over-share your own views before you\'ve heard theirs',
      'Make promises you aren\'t sure you can keep',
    ],
    questions: [
      'What\'s most important in your work right now?',
      'Where do you feel most energized? Most frustrated?',
      'What would make you more effective in your role?',
      'What\'s your read on our highest-leverage opportunities?',
      'What do you want me to know about how you work best?',
      'What does success look like in your role from your perspective?',
    ],
  },
  {
    id: 'skip-level',
    title: 'Skip-Level Conversations',
    timing: 'Days 15–45',
    duration: '30–45 min',
    purpose:
      'Get unfiltered signal from people who won\'t filter through your direct reports. This is intelligence gathering, not evaluation.',
    agenda: [
      { time: '0–5 min', topic: 'Framing', notes: 'Be explicit: this is a listening conversation, not a performance review.' },
      { time: '5–25 min', topic: 'Their experience', notes: 'What\'s working, what\'s hard, what they wish was different.' },
      { time: '25–35 min', topic: 'Their read on the team', notes: 'What do they see the team doing well? Where do they see friction or gaps?' },
      { time: '35–45 min', topic: 'What would help', notes: 'What would make them more effective?' },
    ],
    doList: [
      'Be explicit that this is confidential',
      'Ask for their honest perspective, not the party line',
      'Look for patterns across conversations, not single data points',
      'Thank them for their candor and time',
    ],
    dontList: [
      'Use what you hear to take action without triangulating first',
      'Share what specific individuals told you',
      'Ask them to evaluate their manager directly',
      'Take notes in a way that looks like documentation against someone',
    ],
    questions: [
      'What\'s the team\'s biggest opportunity right now?',
      'What gets in the way of doing your best work?',
      'If you were me, what would you focus on first?',
      'What\'s not being talked about that probably should be?',
      'What do you wish leadership understood about what you do?',
    ],
  },
  {
    id: 'peer-alignment',
    title: 'Peer Alignment Meeting',
    timing: 'Days 10–45',
    duration: '45 min',
    purpose:
      'Build lateral trust before you need it. Understand how your team\'s work connects to theirs. Find natural areas of collaboration.',
    agenda: [
      { time: '0–5 min', topic: 'Personal connection', notes: 'Who are they? Their path here?' },
      { time: '5–20 min', topic: 'Their world', notes: 'Their priorities, their challenges, what success looks like for them.' },
      { time: '20–35 min', topic: 'Intersections', notes: 'Where do your teams connect? Where is there friction or dependency?' },
      { time: '35–45 min', topic: 'How you can help each other', notes: 'What would make this relationship useful for both of you?' },
    ],
    doList: [
      'Come with genuine curiosity about their work',
      'Look for where you can give before you ask',
      'Be transparent about your learning agenda',
      'Follow up on commitments quickly — this builds trust faster than anything',
    ],
    dontList: [
      'Treat it as a transactional meeting',
      'Get into political conversations about other leaders',
      'Overpromise what you\'ll do together',
    ],
    questions: [
      'What are you most focused on this quarter?',
      'Where do our teams most depend on each other?',
      'Where have you seen friction between our teams in the past?',
      'What would make our relationship most useful to you?',
      'What do you wish your peer leaders understood about your team\'s work?',
    ],
  },
  {
    id: 'manager-checkin',
    title: 'Check-In with Your Manager',
    timing: 'Days 30, 60, 90',
    duration: '45–60 min',
    purpose:
      'Align on your progress, surface surprises early, and make sure you\'re solving the right problem. Your manager\'s perception of your performance is data you need.',
    agenda: [
      { time: '0–5 min', topic: 'Where I am', notes: 'Brief update on what you\'ve done and learned.' },
      { time: '5–20 min', topic: 'What I\'m seeing', notes: 'Your synthesis of what\'s most important and what you plan to focus on.' },
      { time: '20–35 min', topic: 'Their read', notes: 'What are they observing? What do they wish you were doing more or less of?' },
      { time: '35–50 min', topic: 'Alignment check', notes: 'Are you focused on the right things? What does success look like to them?' },
      { time: '50–60 min', topic: 'What you need', notes: 'Air cover, resources, decisions you need from them.' },
    ],
    doList: [
      'Come with your own view — don\'t just ask what they think',
      'Surface surprises and problems early, not after they\'ve grown',
      'Be specific about what you\'re asking for',
      'Thank them for what they\'ve done to support you',
    ],
    dontList: [
      'Just give a status report — have a perspective',
      'Hide problems hoping they\'ll resolve',
      'Agree with everything they say without your own point of view',
    ],
    questions: [
      'How do you see my first [30/60/90] days going?',
      'Am I focused on the right things from your perspective?',
      'What are you hearing about my team that I should know?',
      'What do you need from me that you\'re not getting?',
      'What would make you most confident in me at the end of the 100 days?',
    ],
  },
]

// ─── Daily Prompts ────────────────────────────────────────────────────────────

export const DAILY_PROMPTS: Record<number, string> = {
  1:  'What does success look like for you at Day 100? Write it down in as much detail as you can.',
  2:  'Who are the 5 most important relationships you need to build in your first 30 days? Why each one?',
  3:  'What do you need to learn in the next 30 days that you can\'t afford to get wrong?',
  5:  'What assumptions are you bringing into this role that might be wrong?',
  7:  'What did you notice today that surprised you? What does it tell you?',
  10: 'After your first team meeting, what\'s your read on the team\'s energy and confidence?',
  14: 'What are the three most important questions you\'re trying to answer in your listening tour?',
  21: 'What patterns are you seeing across the conversations you\'ve had?',
  30: 'What are the top strengths you\'ve observed? What are the biggest tensions? What are the most exciting opportunities?',
  45: 'Where have you built trust so far? Where do you still need to invest?',
  60: 'Is your manager aligned with how you see your role and priorities? If not, where are the gaps?',
  75: 'What are you avoiding? Why?',
  90: 'What are the 3–5 things that most need to be true for your team to succeed in the next year?',
  100: 'What do you know now that you wish you\'d known on Day 1?',
}

// ─── Key Frameworks ───────────────────────────────────────────────────────────

export const LISTENING_TOUR_QUESTIONS = [
  {
    category: 'The Work',
    questions: [
      'What is this team most proud of?',
      'What are the most important things you\'re working on right now?',
      'Where is there the most friction in getting work done?',
      'What are we not doing that we should be?',
      'What are we doing that we should probably stop?',
    ],
  },
  {
    category: 'The People',
    questions: [
      'Who are the people I should make sure to talk to early?',
      'Who has the trust of the team? Why?',
      'Who has the most to teach me about this organization?',
      'Where do you see the most untapped potential?',
    ],
  },
  {
    category: 'The Org & Culture',
    questions: [
      'How does this organization make decisions?',
      'What does it take to get something done here?',
      'What are the unwritten rules I should know?',
      'What\'s celebrated here? What\'s not tolerated?',
      'What happened here that I should understand?',
    ],
  },
  {
    category: 'For You Specifically',
    questions: [
      'If you were in my role, what would you focus on first?',
      'What\'s not being said that probably should be?',
      'What do you want me to know that I probably don\'t yet?',
      'What would make you say, at Day 100, that I\'ve been successful?',
    ],
  },
]

export const PRIORITY_DISCOVERY_FRAMEWORK = {
  title: 'Priority Discovery Framework',
  description: 'Use this to surface and categorize the priorities you\'re inheriting and discovering.',
  statuses: [
    {
      id: 'inherit',
      label: 'Inherit & Protect',
      description: 'Things that are working. Don\'t break them.',
    },
    {
      id: 'accelerate',
      label: 'Inherit & Accelerate',
      description: 'Good momentum. Add resource or focus.',
    },
    {
      id: 'fix',
      label: 'Fix',
      description: 'Broken or underperforming. Needs attention.',
    },
    {
      id: 'stop',
      label: 'Stop',
      description: 'No longer the right use of effort.',
    },
    {
      id: 'new',
      label: 'New Initiative',
      description: 'Something that doesn\'t exist yet.',
    },
  ],
}
